import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'helpers/api';
import { useNavigate } from "react-router-dom";
import Spinner from 'react-bootstrap/Spinner';
import { Link } from 'react-router-dom';
import paths from 'routes/paths';
import { socket } from '../../socket';
import { storeDayPrograms, storeAtHomePatients } from 'helpers/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { ToneIcon } from 'components/registration-shared/RegistrationUI';
import { storeLoginReleaseNotes } from 'data/changelogData';

const forgotPasswordPaths = {
  simple: paths.simpleForgotPassword,
  split: paths.splitForgotPassword,
  card: paths.cardForgotPassword
};

const LoginForm = ({ hasLabel, layout }) => {
  // State
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    remember: false
  });

  const [spinner, showSpinner] = useState(false);

  const navigate = useNavigate();

  // Handler
  const handleSubmit = e => {
    e.preventDefault();
    showSpinner(true);

    fetch(`${process.env.REACT_APP_SERVER_URL}` + "/users/login",
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 'username': formData.username, 'password': formData.password })
      })
      .then(response => response.json())
      .then(async function (data) {
        const items = data;
        //console.log("Loginform " + items.status);

        if (items.status) {
          localStorage.setItem("accessToken", items.token);
          localStorage.setItem("username", formData.username);
          storeLoginReleaseNotes(items);
          socket.emit('authenticate', { token: items.token });

          let sendingObj = {
            "masters": ["order_types", "order_status", "delivery_types", "delivery_locations", "pharmacy_attentions", "callback_masters"]
          }
          await axios.post(`${process.env.REACT_APP_SERVER_URL}/orders/fetch-masters`, sendingObj, { headers: { 'Authorization': `Bearer ${items.token}` } })
            .then((res) => {
              localStorage.setItem("masters", JSON.stringify(res.data.masters));
            }).catch((err) => {
              console.log(err.message);
            })

          //set roleid
          await axios.get(`${process.env.REACT_APP_SERVER_URL}/users/profile`, { headers: { 'Authorization': `Bearer ${items.token}` } })
            .then((res) => {
              //console.log("Roleid of user is ", res.data.group_id);
              localStorage.setItem("roleId", res.data.role_id);
              localStorage.setItem("groupId", res.data.group_id);
              localStorage.setItem("accessControl", res.data.access_control);
              localStorage.setItem("medsheetFormat", res.data.medsheet_format);
              localStorage.setItem("user-name", `${res.data.first_name} ${res.data.last_name}`);
              localStorage.setItem("notificationCount", res.data.unread_notifications?res.data.unread_notifications:0);
              localStorage.setItem(
                'dayPrograms',
                JSON.stringify(Array.isArray(res.data.day_programs) ? res.data.day_programs : [])
              );
              // Group feature flags. The backend now returns the raw
              // is_sharedliving_active boolean for SharedLiving-enabled groups
              // AND mirrors all flags in `access_control` (2=mym, 4=dayprogram,
              // 5=sharedliving). The helpers in helpers/isSharedLivingActive.js
              // read both. Normalize to "1"/"0" so we never store "undefined".
              const flagToString = v => (v === 1 || v === '1' || v === true || v === 'true') ? '1' : '0';
              localStorage.setItem("is_dayprogram_active", res.data.is_dayprogram_active);
              localStorage.setItem("is_sharedliving_active", flagToString(res.data.is_sharedliving_active));
              localStorage.setItem("is_mym_active", res.data.is_mym_active);

              storeDayPrograms({ token: items.token, user: res.data });
              // SharedLiving: persist the caregiver's assigned patients from the
              // profile response (source of truth — not decoded from the JWT).
              storeAtHomePatients({ user: res.data });
              if (items.is_hippa_done === 0) {
                localStorage.setItem("isHippaDone", items.is_hippa_done);
                localStorage.setItem("userId", res.data.id);
              }
              //localStorage.setItem("groupId", 56);
              toast.success(`Logged in as ${formData.username}`, {
                theme: 'colored'
              });

              // reload and navigate to main
              // window.location.href = "/main";
              navigate("/main");
            });


        }
        else {
          if ([1421].includes(items.errorCode)) {
            localStorage.setItem("usernametemp", formData.username);
            localStorage.setItem("hash", items.token);
            toast.error(`${items.message}, Please Change the Password`, {
              theme: 'colored'
            });
            navigate("/change-password")
          }
          else if ([1422].includes(items.errorCode)) {
            localStorage.setItem("hash", items.hash);
            toast.warning(items.message, {
              theme: 'colored'
            });
            navigate("/otp");
          }
          else if ([1417, 1418, 1416].includes(items.errorCode)) {
            localStorage.setItem("hash", items.hash);
            toast.warning(`Email Needs to be verified ${formData.username}, Please Check Inbox For OTP`, {
              theme: 'colored'
            });
            navigate("/otp");
          }
          else {
            showSpinner(false);
            toast.error(items.message);
          }
        }
      })

  };

  const handleFieldChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Username</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? 'Username' : ''}
          value={formData.username}
          name="username"
          onChange={handleFieldChange}
          type="text"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Password</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? 'Password' : ''}
          value={formData.password}
          name="password"
          onChange={handleFieldChange}
          type="password"
        />
      </Form.Group>

      <Form.Group>
        {spinner && (
          <Button variant="primary" disabled className="mt-3 w-100">
            <Spinner
              as="span"
              animation="grow"
              size="sm"
              role="status"
              aria-hidden="true"
            />
            <span className="p-2" style={{ "verticalAlign": "3px" }}>Loading...</span>
          </Button>
        )}
        {!spinner && (
          <Button
            type="submit"
            color="primary"
            className="mt-3 w-100"
            disabled={!formData.username || !formData.password}>
            Log in
          </Button>
        )}
      </Form.Group>
      <Form.Group>
        <Link className="fs-10 text-600" to={{ pathname: forgotPasswordPaths[layout] }}>Forgot Password</Link>
      </Form.Group>

      {/* No account yet: self-registration request, reviewed by an administrator. */}
      <Link to={paths.register} className="mt-4">
        <span className="min-w-0">
          <span >
            New here? Request access
          </span>
        </span>
      </Link>

      <Form.Group className="mt-3 text-center">
        <p style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.3rem' }}>
          For support, contact&nbsp;
          <a href="mailto:support@mapyourmeds.com" style={{ color: '#0d6efd', textDecoration: 'none', fontWeight: 500 }}>
            support@mapyourmeds.com
          </a>
        </p>
        <p className='mt-4' style={{ fontSize: '1rem', color: '#343a40', marginBottom: 0 }}>
          For user management or program allocation queries, contact&nbsp;
          <a href="mailto:usermanagement@mapyourmeds.com" style={{ color: '#0d6efd', textDecoration: 'none', fontWeight: 500 }}>
            usermanagement@mapyourmeds.com
          </a>
        </p>
      </Form.Group>

    </Form>
  );
};

LoginForm.propTypes = {
  layout: PropTypes.string,
  hasLabel: PropTypes.bool
};

LoginForm.defaultProps = {
  layout: 'simple',
  hasLabel: false
};

export default LoginForm;

